import {FC} from 'react';
import {Box} from '@mui/material';

import {ColorLabel, CurrencyValue} from '@shared/components';
import {TIME_FORMAT} from '@shared/constants';
import {formatInTz} from '@shared/utils';
import {AutoCancelStatus, Order} from '../types';

import {COLOR__BACK, COLOR__GRAY} from '@/theme/colors';

// Exact colors requested for these badges (0xFF34C759 etc. minus the alpha)
const COLOR__TP = '#34C759';
const COLOR__TP_LIGHT = '#E3F9E9';
const COLOR__SL = '#B01F80';
const COLOR__SL_LIGHT = '#F5DDED';
const COLOR__AC = '#00C7BE';
const COLOR__AC_LIGHT = '#DFF9F7';

type Props = Pick<Order, 'auto_cancel' | 'stop_loss' | 'take_profit'>;

export const TriggerBadges: FC<Props> = ({
  auto_cancel,
  stop_loss,
  take_profit,
}) => {
  if (!auto_cancel && !stop_loss && !take_profit) {
    return null;
  }

  // Executed/voided triggers are already resolved, so they no longer need to stand out
  const isResolved = (status: AutoCancelStatus) =>
    status === AutoCancelStatus.executed || status === AutoCancelStatus.voided;

  const isAutoCancelResolved = !!auto_cancel && isResolved(auto_cancel.status);
  const isTakeProfitResolved = !!take_profit && isResolved(take_profit.status);
  const isStopLossResolved = !!stop_loss && isResolved(stop_loss.status);

  return (
    <Box display="flex" gap={0.5} flexWrap="wrap">
      {take_profit && (
        <ColorLabel
          display="inline-flex"
          alignItems="center"
          gap={0.5}
          bgcolor={isTakeProfitResolved ? COLOR__BACK : COLOR__TP_LIGHT}
          color={isTakeProfitResolved ? COLOR__GRAY : COLOR__TP}
        >
          <span>TP</span>
          <CurrencyValue value={take_profit.price} />
        </ColorLabel>
      )}

      {stop_loss && (
        <ColorLabel
          display="inline-flex"
          alignItems="center"
          gap={0.5}
          bgcolor={isStopLossResolved ? COLOR__BACK : COLOR__SL_LIGHT}
          color={isStopLossResolved ? COLOR__GRAY : COLOR__SL}
        >
          <span>SL</span>
          <CurrencyValue value={stop_loss.price} />
        </ColorLabel>
      )}

      {auto_cancel && (
        <ColorLabel
          display="inline-flex"
          alignItems="center"
          gap={0.5}
          bgcolor={isAutoCancelResolved ? COLOR__BACK : COLOR__AC_LIGHT}
          color={isAutoCancelResolved ? COLOR__GRAY : COLOR__AC}
        >
          <span>AC</span>
          <span>{formatInTz(auto_cancel.timeoutAt, TIME_FORMAT)}</span>
        </ColorLabel>
      )}
    </Box>
  );
};
